import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IVideoProps, IVimeoVideo } from "./Video.interface";
import Player from "@vimeo/player";
import { ModuleContext } from "../../../shared/providers/module/module.provider";
import { ExternalPlatforms, ComponentTypes } from "../../../shared/interfaces/modules.interface";
import { TranslationContext } from "../../../shared/providers/translation/translation.provider";
import { delay, secondsToTimeFormat } from "../../../shared/helpers/utils";
import { LoadingContext } from "../../../shared/providers/loading/loading.provider";
import Placeholder from "../../../shared/assets/imgs/placeholder.svg";
import Link from "../../Link/Link";
import { CONTENT_BASE_URL } from "../../../shared/constants";
import { useVimeo } from "../../../shared/hooks/useVimeo";

function Video(props: IVideoProps) {
    const IS_DEBUG = false;
    const { lang } = useContext(TranslationContext);
    const { translate } = useContext(TranslationContext);
    const { setProgress, currentUnitId, module } = useContext(ModuleContext);
    const [videoId, setVideoId] = useState<string>();
    const [duration, setDuration] = useState<number>(0);
    const [timestamp, setTimestamp] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { setIsLoading } = useContext(LoadingContext);

    const [events, setEvents] = useState<string[]>([]);

    const parseId = useCallback((url: string) => {
        const params = new URL(url).pathname;
        const id = params.split("/").pop();

        return id ?? "";
    }, []);

    useEffect(() => {
        setVideoId(parseId(props.url));
    }, [props.url]);

    const { data: vimeoVideo, isLoading } = useVimeo(videoId);

    useEffect(() => {
        setIsLoading(isLoading);
    }, [isLoading]);

    useEffect(() => {
        // CHECK WINDOW VISIBILITY
        // PAUSE VIDEO WHEN APP ON BACKGROUND
        const checkVisibility = () => {
            if (document.visibilityState === "visible") return;
            videoRef.current?.pause();
        };
        document.addEventListener("visibilitychange", checkVisibility);

        return () => document.removeEventListener("visibilitychange", checkVisibility);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        setIsLoading(true);

        const handleVideoTimestamp = (e: Event) => {
            const video = e.target as HTMLVideoElement;
            const duration = video.duration ?? 1;
            const percent = video.currentTime / duration;

            setPercentage(Math.floor(percent * 100));
            setCurrentTime(Math.round(video.currentTime));

            if (percent >= 1) handleExitFullscreen(undefined, true);
        };

        const handleEnterFullscreen = async () => {
            try {
                const video = videoRef.current;
                if (!video) return;
                const { currentTime, duration } = video;
                const percentage = currentTime / duration;
                if (percentage > 0.95) video.currentTime = 0;

                setEvents((v) => [...v, "video play"]);
                video.play();
            } catch (e: any) {
                setEvents((v) => [...v, `ERROR: ${e.message}`]);
            }
        };

        const handleExitFullscreen = async (event?: Event, exitFullscreen?: boolean) => {
            try {
                setEvents((v) => [...v, "video paused"]);
                video.pause();
                
                const isFullscreen = document.fullscreenElement || exitFullscreen;
                
                setEvents((v) => [...v, `isFullscreen: ${isFullscreen}`]);

                if ("exitFullscreen" in document && isFullscreen) {
                    setEvents((v) => [...v, "request document.exitFullscreen"]);
                    await document.exitFullscreen();
                } else if ("webkitExitFullscreen" in document && isFullscreen) {
                    setEvents((v) => [...v, "request document.webkitExitFullscreen"]);
                    await (document as any).webkitExitFullscreen();
                }
            } catch (e: any) {
                setEvents((v) => [...v, `ERROR: ${e.message}`]);
            }
        };

        const handleVideoFullscreen = async (e: Event) => {
            setEvents((v) => [...v, "trigger: handleVideoFullscreen"]);
            try {
                const video = videoRef.current;
                
                if (!video) return;
                const isFullscreen = document.fullscreenElement;
                
                if (isFullscreen) {
                    setEvents((v) => [...v, "enter fullscreen"]);
                    handleEnterFullscreen();
                } else {
                    setEvents((v) => [...v, "exit fullscreen"]);
                    handleExitFullscreen();
                }
            } catch (e) {
                console.log(e);
            }
        };

        const handleVideoLoaded = () => {
            setEvents((v) => [...v, "videoLoaded"]);
            setDuration(video.duration);
            setIsLoading(false);
        };

        video.addEventListener("loadeddata", handleVideoLoaded);
        video.addEventListener("timeupdate", handleVideoTimestamp);

        video.addEventListener("webkitfullscreenchange", handleVideoFullscreen);
        video.addEventListener("fullscreenchange", handleVideoFullscreen);
        video.addEventListener("webkitbeginfullscreen", () => {
            setEvents((v) => [...v, "webkitbeginfullscreen"]);
            handleEnterFullscreen();
        });
        video.addEventListener("webkitendfullscreen", () => {
            setEvents((v) => [...v, "webkitendfullscreen"]);
            handleExitFullscreen();
        });

        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!module || !module.unitsProgress || !videoRef.current) return;
        const unitProgress = module.unitsProgress.find((unit) => unit.id === currentUnitId);

        const itemProgress = unitProgress?.items.find(
            (item) => item.strapiItemId === props.id && item.type === props.type
        );

        if (!itemProgress) return;
        const timestamp = Math.round((itemProgress.progress * duration) / 100);
        setPercentage(itemProgress.progress ?? 0);

        setCurrentTime(timestamp);
        setTimestamp(timestamp);

        videoRef.current.currentTime = timestamp;
    }, [videoRef.current, duration, module, currentUnitId]);

    useEffect(() => {
        // UPDATE DB EVERY 10s
        if ((currentTime % 10 === 0 || duration === currentTime) && lang && videoId) {
            setProgress({
                unitId: props.unitId,
                itemId: props.id,
                progress: percentage,
                itemType: ComponentTypes.VIDEO,
                itemLocale: lang,
                externalPlatform: ExternalPlatforms.VIMEO,
                externalId: videoId
            });
        }
    }, [currentTime]);

    const handlePlay = async () => {
        try {
            if ((videoRef.current as any)?.webkitEnterFullscreen) {
                setEvents((v) => [...v, "webkitEnterFullscreen"]);
                await (videoRef.current as any)?.webkitEnterFullscreen();
            } else if ((videoRef.current as any)?.webkitRequestFullScreen) {
                setEvents((v) => [...v, "webkitRequestFullScreen"]);
                await (videoRef.current as any)?.webkitRequestFullScreen();
            } else if (videoRef.current?.requestFullscreen) {
                setEvents((v) => [...v, "requestFullscreen"]);
                await videoRef.current?.requestFullscreen();
            }

            setEvents((v) => [...v, "play"]);
            await videoRef.current?.play();
        } catch (e: any) {
            setEvents((v) => [...v, `ERROR: ${e.message}`]);
        }
    };

    return (
        <section className="unit_item container-fluid gx-2">
            <div className="row gx-0">
                <div className="col-12">
                    <span className="d-flex justify-content-end color-neutrals-neutral text-bd4-rg align-items-center gap-01 mb-02">
                        <i className="icon icon-12 icon-Time" />
                        <span>{secondsToTimeFormat(currentTime)}</span>
                        <span>/</span>
                        <span>{secondsToTimeFormat(duration)}</span>
                    </span>
                    <div className="video_thumb">
                        <img
                            src={
                                props.thumbnail
                                    ? `${CONTENT_BASE_URL}${props.thumbnail.url}`
                                    : Placeholder
                            }
                            aria-label={translate("common.play", "Play")}
                        />
                        <button
                            type="button"
                            className="button_play"
                            aria-label={translate("common.play", "Play")}
                            onClick={handlePlay}>
                            <i className="icon icon-48 icon-Play--filled color-neutrals-white" />
                        </button>
                    </div>
                    <video
                        ref={videoRef}
                        src={vimeoVideo?.success ? vimeoVideo.data.link : undefined}
                        width="640"
                        height="360"
                        style={{ width: 0, height: 0 }}
                        className="item_video"
                        disablePictureInPicture
                        controlsList="nodownload nofullscreen"
                        controls
                    />
                    <div className="card d-flex gap-02 align-items-center mt-n1">
                        <progress className="card_progress" value={percentage} max="100"></progress>
                        <span className="text-bd4-md color-neutrals-neutral">{percentage}%</span>
                    </div>
                    {IS_DEBUG && (
                        <ul>
                            {events.map((ev, i) => (
                                <li key={i}>{ev}</li>
                            ))}
                        </ul>
                    )}
                    {!!props.transcript && (
                        <div className="mt-2 d-flex">
                            <Link
                                target="_blank"
                                to={CONTENT_BASE_URL + props.transcript.url}
                                customCss="py-03 flex-grow-1 justify-content-start"
                                label="Read our audio transcript"
                                variant="link"
                                icon="Document"
                                download
                            />
                            <Link
                                target="_blank"
                                to={CONTENT_BASE_URL + props.transcript.url}
                                variant="link"
                                icon="Chevron--right"
                                iconSize={16}
                                download
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default Video;
